/**
 * Leitura ÚNICA do erro da casa (Família B do dossiê de furos).
 *
 * O backend fala em três dialetos dentro de `detail`:
 *   - string simples ("cliente não encontrado")
 *   - objeto da casa ({erro, motivo, como_resolver?, sugestoes?})
 *   - lista de validação do FastAPI/Pydantic ([{loc, msg, type}, ...])
 * Cada tela lia isso do seu jeito — e a maioria descartava (F-384) ou jogava
 * o objeto cru no JSX. Este helper devolve SEMPRE uma frase legível em pt-BR;
 * quem precisar dos campos estruturados lê `err.body.detail` direto.
 */
import { ApiError } from '@/services/api';

type DetalheObjeto = {
  erro?: unknown;
  motivo?: unknown;
  como_resolver?: unknown;
  message?: unknown;
};

function textoDe(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function detalheLegivel(err: unknown, fallback = 'Falha inesperada — tente novamente.'): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
      // Validação Pydantic: campo → mensagem, uma linha por problema.
      const linhas = detail
        .map((d) => {
          const loc = Array.isArray((d as { loc?: unknown[] }).loc)
            ? (d as { loc: unknown[] }).loc.filter((p) => p !== 'body').join('.')
            : '';
          const msg = textoDe((d as { msg?: unknown }).msg);
          return msg ? (loc ? `${loc}: ${msg}` : msg) : null;
        })
        .filter(Boolean);
      if (linhas.length) return linhas.join(' · ');
    }
    if (detail && typeof detail === 'object') {
      const d = detail as DetalheObjeto;
      const erro = textoDe(d.erro) ?? textoDe(d.message);
      const motivo = textoDe(d.motivo);
      const como = textoDe(d.como_resolver);
      const partes = [erro, motivo, como].filter(Boolean);
      if (partes.length) return partes.join(' — ');
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
